import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { WBSButton } from '@/common/WBSButton';
import { Card } from '~/styles/layoutElements';
import { formatString } from '~/utils/stringFormat';
import type { Lesson } from './types';

/**
 * The tile is the same for every viewer: a live Review link and no per-user
 * state.
 *
 * Legacy cached the directory HTML per language only, so whichever viewer
 * rendered it first set the progress state everyone saw; porting that would
 * badge lessons complete or locked for everyone. So nothing here reads progress
 * or access, and the lesson route sends a viewer without access to the parent
 * course.
 */
export function LessonCard({ lesson }: { lesson: Lesson }) {
  const { t } = useTranslation();

  // From the payload, not a per-tile topics request: `topics_count` is counted
  // server-side with the same draft-visibility scope as `lesson_topics`, and
  // isn't capped by a page limit.
  const topicsCount = lesson.topics_count;

  return (
    <Card>
      <ThumbWrapper>
        {lesson.image ? (
          <Thumb src={lesson.image} alt={lesson.title} />
        ) : (
          <PlaceholderThumb />
        )}
        {topicsCount > 0 && (
          <TopicsBadge>
            {topicsCount === 1
              ? `${topicsCount} ${t('wbs.topic')}`
              : formatString(t('wbs.d-topics'), String(topicsCount))}
          </TopicsBadge>
        )}
      </ThumbWrapper>
      <CardBody>
        <CardTitle>{lesson.title}</CardTitle>
        <CardUnderline />
        <CtaWrapper>
          <Link to="/lessons/$lessonSlug/" params={{ lessonSlug: lesson.slug }}>
            <WBSButton color="var(--blue)" wide>
              {t('wbs.review')}
            </WBSButton>
          </Link>
        </CtaWrapper>
      </CardBody>
    </Card>
  );
}

const ThumbWrapper = styled.div`
  position: relative;
`;

const Thumb = styled.img`
  border-radius: 5px;
  height: 142px;
  object-fit: cover;
  position: relative;
  width: 100%;
`;

const PlaceholderThumb = styled.div`
  background: var(--grey-200);
  border-radius: 5px;
  height: 142px;
  width: 100%;
`;

const TopicsBadge = styled.div`
  background: var(--orange);
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 0;
  border-right: none;
  border-top-left-radius: 6px;
  border-top-right-radius: 0;
  box-shadow: 0px 2px 2px 0px var(--shadow-color);
  color: var(--white);
  font-family: var(--raleway);
  font-size: 12px;
  padding: 3px 9px 3px 12px;
  position: absolute;
  right: 0;
  top: 24px;
  white-space: nowrap;
  z-index: 2;
`;

const CardBody = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: space-between;
  padding: 6px 8px 8px 8px;
  position: relative;
`;

const CardTitle = styled.div`
  -webkit-box-orient: vertical;
  color: var(--grey-1000);
  display: -webkit-box;
  font-family: var(--raleway);
  font-size: 15px;
  font-weight: 600;
  -webkit-line-clamp: 2;
  line-height: 1.2;
  min-height: calc(1.2em * 2);
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardUnderline = styled.div`
  background: var(--wbs-red);
  height: 4px;
  margin: 15px 0 10px 0;
  width: 40px;
`;

const CtaWrapper = styled.div`
  align-items: flex-end;
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  margin-top: 3px;
  min-height: 40px;
`;
